import { type ReactNode, useEffect, useRef } from 'react'

import { isEmpty } from 'lodash'
import { useTranslation } from 'react-i18next'

import { useLoadingContext } from 'context/loadingContext'
import { useGetUserProfile } from 'helpers/hooks'
import { setDateTimeLocale } from 'lib/dayjs'
import { useNotifications } from 'services/notifications/notificationService'
import { disconnectPusher } from 'services/pusherclient/pusherClient'
import { useAccessToken, useSetUserProfile, useUserProfile } from 'store'
import { usePendingNotificationsStore } from 'store/pendingNotificationsStore'
import { useProfileCacheStore } from 'store/profileCacheStore'
import type { IProfile } from 'types/interfaces/IProfile'

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { i18n } = useTranslation()

  const accessToken = useAccessToken()
  const userProfile = useUserProfile()
  const { handleGlobalLoading } = useLoadingContext()

const { data, isLoading } = useGetUserProfile(!!accessToken, !!userProfile?.id)

  useNotifications(accessToken)

  const setCachedProfile = useProfileCacheStore(state => state.setProfile)

  // prevent overwriting live pusher bumps on refetch
  const hydratedCountsForUserRef = useRef<number | null>(null)

  useEffect(() => {
    if (!accessToken) {
      disconnectPusher()
      hydratedCountsForUserRef.current = null
      usePendingNotificationsStore.getState().hydrate({
        notifications: 0,
        messages: 0,
      })
    }
  }, [accessToken])

  useEffect(() => {
    if (!data) return

    // Update Zustand auth store
    useSetUserProfile(data as unknown as IProfile)

    // Cache profile
    if ((data as any).username) {
      setCachedProfile((data as any).username, data as unknown as IProfile, (data as any).id)
    }

    //  Hydrate initial pending counts once
    const myId = (data as any)?.id ?? null
    if (myId && hydratedCountsForUserRef.current !== myId) {
      hydratedCountsForUserRef.current = myId
      const pending = (data as any)?.pending_counts
      if (pending) {
        usePendingNotificationsStore.getState().hydrate({
          notifications: Number(pending.notifications ?? 0),
          messages: Number(pending.messages ?? 0),
        })
      }
    }

    // Language
    if (!isEmpty((data as any).language)) {
      const langCode = (data as any).language.code.toLowerCase()
      i18n.changeLanguage(langCode)
      setDateTimeLocale(langCode)
    }

    // Loading indicator (only tied to profile load)
    handleGlobalLoading(isLoading)
  }, [data, isLoading])

  if (isLoading) return null
  return <>{children}</>
}
